<template>
  <header class="header">
    <div class="header__title">
      <h2>{{ $t(pageTitleHeading()) }}</h2>
    </div>
    <SearchBar class="header__search" />
  </header>

  <NotificationBanner
    v-if="showC1VBetaBanner"
    class="notification-banner--beta"
    :buttonLink="`/bookings/${currentContent}`"
    :buttonText="$t('manageBookings.textButton')"
    @notification-tracking="betaBannerNavigation"
  />

  <div v-if="brandCode !== 'seau'" class="content-switcher" data-test="content-switcher">
    <mc-segmented-control
      class="content-switcher__component"
      width="full-width"
      appearance="primary"
      fit="small"
      @listchange="changeContent"
    >
      <mc-segmented-control-item
        icon="vessel-front"
        value="ocean"
        :label="`${$t('common.ocean')} ${oceanTotalCount}`"
        class="content-switcher__button"
        :selected="currentContent === 'ocean'"
        data-test="content-switcher-ocean"
      >
      </mc-segmented-control-item>
      <mc-segmented-control-item
        icon="pallet"
        value="lcl"
        class="content-switcher__button"
        :label="`${$t('common.lcl')} ${lclCount || lclCount === 0 ? '(' + lclCount + ')' : ''}`"
        :selected="currentContent === 'lcl'"
        data-test="content-switcher-lcl"
      >
      </mc-segmented-control-item>
      <mc-segmented-control-item
        icon="airplane"
        value="air"
        class="content-switcher__button"
        :label="`${$t('common.air')} ${airCount || airCount === 0 ? '(' + airCount + ')' : ''}`"
        :selected="currentContent === 'air'"
        data-test="content-switcher-air"
      >
      </mc-segmented-control-item>
    </mc-segmented-control>

    <span class="d-block">
      <mc-icon
        icon="info-circle"
        color="var(--mds_brand_appearance_neutral_default_text-color)"
        class="cursor-pointer d-block-md"
        data-test="coach-mark-transport-plan"
        @click="handleCoachMarkClick"
      />
    </span>
  </div>

  <section v-if="isExport && currentContent === 'lcl' && brandCode !== 'seau'" data-test="lcl-section">
    <div data-test="overview-tab" class="mt-4">
      <mc-tab-bar :currentindex="currentTabIndexLcl" @tabchange="updateRouteLcl">
        <mc-tab slot="tab" ref="shipmentsTabLcl" :label="$t('tab.shipments')" data-test="shipments-tab-lcl" />
        <div slot="panel">
          <div class="mb-4">{{ $t("shipments.description") }}</div>
          <Lcl v-if="$route.path === '/export/lcl' || $route.path === '/export/lcl/shipments'" key="shipments" />
        </div>
        <mc-tab
          slot="tab"
          ref="unconfirmedBookingsTabLcl"
          class="unconfirmed-bookings-tab-lcl"
          :label="$t('tab.unconfirmedBookings')"
          data-test="unconfirmed-bookings-tab-lcl"
        />
        <div slot="panel">
          <div class="mb-4">{{ $t("unconfirmed.description", { brand: brand }) }}</div>
          <Lcl v-if="$route.path === '/export/lcl/unconfirmedbookings'" key="unconfirmedbookings" />
        </div>
        <mc-tab
          slot="tab"
          ref="cancelledBookingsTabLcl"
          class="cancelled-bookings-tab-lcl"
          :label="$t('tab.cancelledBookings')"
          data-test="cancelled-bookings-tab-lcl"
        />
        <div slot="panel">
          <div class="mb-4">{{ $t("cancelled.description") }}</div>
          <Lcl v-if="$route.path === '/export/lcl/cancelledbookings'" key="cancelledbookings" />
        </div>
      </mc-tab-bar>
    </div>
  </section>

  <div
    v-if="!isExport && currentContent === 'lcl' && brandCode !== 'seau'"
    class="lcl-shipments"
    data-test="lcl-section"
  >
    <h4>
      {{ $t("tab.shipments") }}
    </h4>
    <p class="mb-4">{{ $t("shipments.description") }}</p>

    <Lcl />
  </div>

  <div v-if="currentContent === 'air' && brandCode !== 'seau'" class="mt-6" data-test="air-section">
    <h4>
      {{ $t("tab.shipments") }}
    </h4>
    <p class="mb-4">{{ $t("shipments.description") }}</p>

    <Air />
  </div>

  <section v-if="currentContent === 'ocean'" data-test="ocean-section">
    <div data-test="filters">
      <GlobalFilter />
    </div>
    <div data-test="overview-tab" class="mt-4">
      <mc-tab-bar :currentindex="currentTabIndexOcean" @tabchange="updateRouteOcean">
        <mc-tab slot="tab" ref="shipmentsTab" :label="$t('tab.shipments')" data-test="shipmentsTab" />
        <div slot="panel" class="">
          <OceanShipments v-if="$route.params?.tab === 'shipments' || !$route.params?.tab" />
        </div>
        <mc-tab slot="tab" ref="etaChangesTab" :label="$t('tab.etaChanges')" data-test="etaChangesTab" />
        <div slot="panel">
          <ETAChanges v-if="$route.params?.tab === 'etachanges'" />
        </div>
        <mc-tab
          v-if="isExport"
          slot="tab"
          ref="unconfirmedBookingsTab"
          :label="$t('tab.unconfirmedBookings')"
          data-test="unconfirmedBookingsTab"
        />
        <div slot="panel">
          <UnconfirmedBookings v-if="$route.params?.tab === 'unconfirmedbookings'" />
        </div>
        <mc-tab v-if="isExport" slot="tab" ref="documentsTab" :label="$t('tab.documents')" data-test="documentsTab" />
        <div slot="panel">
          <Documents v-if="$route.params?.tab === 'documents'" />
        </div>
      </mc-tab-bar>
    </div>
  </section>
</template>
<script>
import CONSTANTS from "@/lib/CONSTANTS";
import { injectionKey } from "@maersk-global/vue-plugin-featureflags";
import "@maersk-global/mds-components-core/mc-segmented-control";
import "@maersk-global/mds-components-core/mc-segmented-control-item";
import SearchBar from "@/components/search/SearchBar.vue";
import GlobalFilter from "@/components/filters/GlobalFilter.vue";
import OceanShipments from "@/components/OceanShipments.vue";
import ETAChanges from "@/components/ETAChanges.vue";
import UnconfirmedBookings from "@/components/UnconfirmedBookings.vue";
import NotificationBanner from "@/components/NotificationBanner.vue";
import Lcl from "@/components/Lcl.vue";
import Air from "@/components/Air.vue";
import Documents from "@/components/Documents.vue";
import { showCoachMarks } from "@/lib/coachMarks.js";
import { mapActions, mapState } from "pinia";
import { airLcl } from "@/store/airLcl";
import { customer } from "@/store/customers";
import { shipments } from "@/store/shipments";
import { getBrandFromHostname } from "@maersk-global/shared-js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export default {
  name: "ShipmentOverview",
  components: {
    Documents,
    UnconfirmedBookings,
    OceanShipments,
    GlobalFilter,
    SearchBar,
    Lcl,
    Air,
    ETAChanges,
    NotificationBanner
  },
  inject: {
    injected: { from: injectionKey }
  },
  data() {
    return {
      activeTabIndex: "0",
      currentContent: "ocean",
      tabsOcean: ["shipments", "etachanges", "unconfirmedbookings", "documents"],
      tabsLcl: ["shipments", "unconfirmedbookings", "cancelledbookings"],
      showCoachMarks,
      coachMarkSOItem: [
        {
          text: this.$t("shipments.coachMarks.text", {
            sharedDate: `${dayjs().subtract(3, "months").format("DD MMM YYYY")} - ${dayjs()
              .add(3, "months")
              .format("DD MMM YYYY")}`
          }),
          selector: ".content-switcher__component",
          position: "bottom",
          seen: window.localStorage.setItem("so_content_switch_cm", "false")
        }
      ]
    };
  },
  computed: {
    ...mapState(airLcl, ["airCount", "lclCount"]),
    ...mapState(shipments, ["totalCountAll", "totalCount"]),
    ...mapState(customer, ["getCustomerCountryCode", "getCustomerCountryName"]),
    currentTabIndexOcean() {
      if (this.tabsOcean?.indexOf(this.$route.params?.tab) > -1) {
        return this.tabsOcean.indexOf(this.$route.params?.tab);
      }
      return 0;
    },
    currentTabIndexLcl() {
      if (this.tabsLcl?.indexOf(this.$route.params?.tab) > -1) {
        return this.tabsLcl.indexOf(this.$route.params?.tab);
      }
      return 0;
    },
    isExport() {
      return this.$route?.path?.toLowerCase()?.includes?.("export");
    },
    brandCode() {
      return getBrandFromHostname(window?.location)?.brandCode?.toLowerCase();
    },
    showFeedback() {
      const feedbackExportBanner = this.injected?.getFlag("shipment-overview-feedback-banner", false).value;
      const feedbackImportBanner = this.injected?.getFlag("shipment-overview-feedback-banner-import", false).value;

      return (
        (feedbackExportBanner && !localStorage.getItem("SO_FeedbackExportClick") && this.isExport) ||
        (feedbackImportBanner && !localStorage.getItem("SO_FeedbackImportClick") && !this.isExport)
      );
    },
    shipmentCountTimeframe() {
      return this.isExport
        ? {
            departingFromDate: dayjs().utc().subtract(3, "months").format("YYYY-MM-DD"),
            departingToDate: dayjs().utc().add(3, "months").format("YYYY-MM-DD")
          }
        : {
            arrivingFromDate: dayjs().utc().subtract(3, "months").format("YYYY-MM-DD"),
            arrivingToDate: dayjs().utc().add(3, "months").format("YYYY-MM-DD")
          };
    },
    oceanTotalCount() {
      // if total count all is 10000 we show (10000+)
      // otherwise we show (totalCountAll)
      // if there is no total count, we return an empty string
      return this.totalCountAll || this.totalCountAll === 0
        ? this.totalCountAll === 10000
          ? "(10000+)"
          : `(${this.totalCountAll})`
        : "";
    },
    lclUnconfirmedBookingsCoachmarkSeen() {
      return Boolean(window.localStorage.getItem("so_unconfirmed_bookings_tab_lcl_cm"));
    },
    brand() {
      return getBrandFromHostname(window.location)?.commercialName;
    },
    showC1VBetaBanner() {
      return this.brandCode === "maeu";
    }
  },
  watch: {
    currentContent: {
      handler(value) {
        if (value === "lcl") {
          this.showUnconfirmedBookingsLclCoachmark();
        }
      }
    }
  },
  async created() {
    this.checkTimeFrameFromLocalStorage();
    if (!this.totalCount) {
      await this.getBookings();
    }
    if (!this.airCount) {
      await this.getAirCount();
    }
    if (!this.lclCount) {
      await this.getLclCount();
    }
    if (!this.totalCountAll) {
      await this.getBookingsCount(this.shipmentCountTimeframe);
    }
  },
  async mounted() {
    await this.setCurrentContent();
    await this.getCustomerInfo();

    // Watch all counts and only track the data after the API try/catch update the count values
    this.$watch(
      (vm) => [vm.totalCountAll, vm.airCount, vm.lclCount],
      (val) => {
        if (!val.some((v) => v === null)) {
          this.$sendGAEvent({
            event: "DQCS",
            category: "SO_ShipmentsCount",
            action: "View",
            label: {
              data: "SoOceanAirLclShipmentCounts",
              oceanTotalCount: this.totalCountAll,
              lclCount: this.lclCount,
              airCount: this.airCount
            }
          });
          this.$sendGAEvent({
            event: "DQCS",
            category: "SO_FirstLoadInfo",
            action: "View",
            label: {
              type: this.currentContent,
              customerCountry: this.getCustomerCountryName,
              timeFrame: this.checkTimeFrameFromLocalStorage(),
              oceanCount: this.totalCountAll,
              lclCount: this.lclCount,
              airCount: this.airCount
            }
          });
        }
      },
      {
        immediate: true, // run immediately
        deep: true // detects changes inside objects. not needed here, but maybe in other cases
      }
    );
  },
  updated() {
    this.showUnconfirmedBookingsLclCoachmark();
  },
  methods: {
    ...mapActions(airLcl, ["getAirCount", "getLclCount"]),
    ...mapActions(shipments, ["getBookings", "getBookingsCount", "checkTimeFrameFromLocalStorage", "timeFrame"]),
    ...mapActions(customer, ["getCustomerInfo"]),
    updateRouteOcean(e) {
      if (this.$route.params?.tab !== this.tabsOcean?.[e?.detail]) {
        let path;
        if (this.tabsOcean?.indexOf(this.$route.params?.tab) > -1) {
          path = this.$route.path.replaceAll("/" + this.$route.params?.tab, "");
        } else {
          path = this.$route.path;
        }
        this.$router.push({
          path: `${path}/${this.tabsOcean[e.detail]}`
        });
        this.$sendGAEvent({
          event: "DQCS",
          category: "SO_Tab",
          action: "Click",
          label: {
            data: "SoTabClicked",
            listContent: this.currentContent,
            isExport: !!this.isExport,
            tab: this.tabsOcean?.[e?.detail]
          }
        });
      }
    },
    updateRouteLcl(e) {
      if (this.$route.params?.tab !== this.tabsLcl?.[e?.detail]) {
        let path;
        if (this.tabsLcl?.indexOf(this.$route.params?.tab) > -1) {
          path = this.$route.path.replaceAll("/" + this.$route.params?.tab, "");
        } else {
          path = this.$route.path;
        }
        this.$router.push({
          path: `${path}/${this.tabsLcl[e.detail]}`
        });
        this.$sendGAEvent({
          event: "DQCS",
          category: "SO_Tab",
          action: "Click",
          label: {
            data: "SoTabClickedLcl",
            listContent: this.currentContent,
            isExport: !!this.isExport,
            tab: this.tabsLcl?.[e?.detail]
          }
        });
      }
    },
    setCurrentContent() {
      if (this.$route.path.includes("lcl")) {
        this.currentContent = "lcl";
      } else if (this.$route.path.includes("air")) {
        this.currentContent = "air";
      } else {
        this.currentContent = "ocean";
      }
    },
    handleCoachMarkClick() {
      this.showCoachMarks(this.coachMarkSOItem, this.$t("common.close"), "SO_Content_Switch");
    },
    routeName() {
      return this.$route.path.split("/").filter(Boolean)[0];
    },
    pageTitleHeading() {
      return CONSTANTS.OVERVIEW_HEADINGS[this.routeName()];
    },
    changeContent(e) {
      // Change the route, which in turn remounts the view. The mounted hook runs which then sets the content to the correct tab.
      const path = this.isExport ? "export" : "import";
      switch (e.detail?.item?.value) {
        case "lcl":
          this.$router.push({ path: `/${path}/lcl` });
          break;
        case "air":
          this.$router.push({ path: `/${path}/air` });
          break;
        default:
          this.$router.push({ path: `/${path}` });
          break;
      }
      this.$sendGAEvent({
        event: "DQCS",
        category: "SO_SegmentedControl",
        action: "Click",
        label: { data: "SoShipmentTypeClicked", type: e.detail?.item?.value, isExport: this.isExport }
      });
    },
    currentFilter() {
      // if it's export, we need to pass departingFromDate and departingToDate
      // if it's import, we need to pass arrivingFromDate and arrivingToDate
      // we get the dates from the filters component
      return this.isExport
        ? {
            departingFromDate: dayjs().utc().format("YYYY-MM-DD"),
            departingToDate: dayjs().utc().add(7, "days").format("YYYY-MM-DD")
          }
        : {
            arrivingFromDate: dayjs().utc().format("YYYY-MM-DD"),
            arrivingToDate: dayjs().utc().add(7, "days").format("YYYY-MM-DD")
          };
    },
    showUnconfirmedBookingsLclCoachmark() {
      const seenCoachmark = Boolean(window.localStorage.getItem("so_unconfirmed_bookings_tab_lcl_cm"));

      setTimeout(() => {
        if (document.querySelector(".unconfirmed-bookings-tab-lcl")?.checkVisibility() && !seenCoachmark) {
          this.showCoachMarks(
            [
              {
                title: this.$t("shipments.coachMarks.unconfirmedBookingTabTitle"),
                text: this.$t("shipments.coachMarks.unconfirmedBookingTabText"),
                selector: ".unconfirmed-bookings-tab-lcl",
                position: "right",
                seen: window.localStorage.setItem("so_unconfirmed_bookings_tab_lcl_cm", "true")
              }
            ],
            this.$t("common.gotIt"),
            "soLclUnconfirmedBookingTabCoachmark",
            this.$sendGAEvent
          );
        }
      }, 1000);
    },
    feedbackBannerTracking() {
      const linkClicked = this.$route?.path?.toLowerCase()?.includes?.("export")
        ? "SO_FeedbackExportClick"
        : "SO_FeedbackImportClick";
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: linkClicked }
      });
      localStorage.setItem(linkClicked, true);
    },
    betaBannerNavigation() {
      this.$sendGAEvent({
        event: "DQCS",
        category: "Component",
        action: "Click",
        label: { data: "C1V_BetaBanner" }
      });
    }
  }
};
</script>
